import React, { createContext, useContext, useState, ReactNode } from 'react';
import { FileMapping } from '../types';

interface WizardContextType {
  activeStep: number;
  selectedFiles: File[];
  fileMappings: FileMapping[];
  setSelectedFiles: (files: File[]) => void;
  setFileMappings: (mappings: FileMapping[]) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
}

const WizardContext = createContext<WizardContextType | undefined>(undefined);

export const useWizard = (): WizardContextType => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};

interface WizardProviderProps {
  children: ReactNode;
}

export const WizardProvider: React.FC<WizardProviderProps> = ({ children }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileMappings, setFileMappings] = useState<FileMapping[]>([]);

  const goToNextStep = () => setActiveStep((previous) => previous + 1);
  const goToPreviousStep = () => setActiveStep((previous) => Math.max(0, previous - 1));

  return (
    <WizardContext.Provider
      value={{
        activeStep,
        selectedFiles,
        fileMappings,
        setSelectedFiles,
        setFileMappings,
        goToNextStep,
        goToPreviousStep,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
};
